import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import axios from '@axios';
import { RootState } from '@redux/reducers';
import {
    ItemResponse,
    ItemResponseApi,
    OrderResponse,
} from '@models/order/items/response';
import {
    defaultOrderRequest,
    Item,
    ItemRequest,
    OrderRequest,
    OrderRequestApi,
} from '@models/order/items/request';
import { addNotification } from '../notification';
import { ThunkCallback } from '../model/thunk-callback';
import {
    PaymentConditionResponse,
    PaymentConditionResponseApi,
} from '@models/order/paymentCondition/response';
import { toQueryParameters } from '@extensions/object';
import { Semaphore } from '@models/order/semaphore/response';
import { Pdiscqt } from '@models/order/params/response';
import { Convenio } from '@models/order/convenio/response';
import { Custo } from '@models/order/custos/response';
import {
    calculateItemCost,
    calculateOrderTotalValue,
} from '@redux/functions/order';
import moment from 'moment';
import { addAvailablePlatform, fetchCustomer, fetchDrafts, fetchOrdersToBonitifaction, requestChangeBranch, selectCustomer, setOrderType } from '.';
import '@extensions/number';
import {
    fetchCampaignsForOrder,
    oldSeletedCampain,
    selectCampaign,
} from '../campaign';
import { translate } from '@components/i18n';
import { formatMoney } from '@utils/money';
import { Moeda, MoedaSimbolo, SelectedMoeda } from '@models/order/moedas/response';
import { CustoRequest } from '@models/order/custos/request';
import { OrderResponseApi } from '@models/order/response';
import { ITEM_STATUS } from 'src/enums/item-status';
import { GROUP_TYPES } from 'src/enums/group-types';
import { EntityString } from '@models/entity';
import { IS_ARG, IS_BR } from '@config/env';
import { fetchBalance, updateUseBalance } from '../balance';
import { getCampaignStrategy } from '@domain/campaign/strategies';
import { FetchParams } from '@domain/campaign/types';

export interface IFilterSelectItems {
    filterBy?: string;
    filterValue: string;
    status?: ITEM_STATUS
    tipoPedido?: GROUP_TYPES
}

// =====================================================================
// MOCK precoBase + custo — uso temporário durante desenvolvimento/testes.
// Quando a API estiver retornando precoBase corretamente, mude para
// `false` para desativar a injeção. Itens que já vierem com precoBase
// válido (> 0) não são alterados, e custos que já estejam cadastrados
// também são preservados — deixar ligado não sobrescreve dados reais,
// mas recomenda-se desligar fora de testes.
// =====================================================================
const MOCK_PRECO_BASE = false;

const mockPrecoBaseIfMissing = (
    items: ItemResponseApi[],
): ItemResponseApi[] => {
    if (!MOCK_PRECO_BASE) return items;
    return items.map((item) => {
        if (item.precoBase && Number(item.precoBase) > 0) return item;
        const randomPrecoBase = Number(
            (Math.random() * 195 + 5).toFixed(2),
        ); // 5.00 – 200.00
        item.precoBase = randomPrecoBase;
        return item;
    });
};

const buildStateWithMockedCustos = (
    state: RootState,
    items: ItemResponseApi[],
): RootState => {
    if (!MOCK_PRECO_BASE) return state;
    const existingCustos = state.selectItems.custos ?? [];
    const existingByNumero = new Set(existingCustos.map((c) => c.numeroItem));
    const mockedCustos: Custo[] = items
        .filter((it) => !existingByNumero.has(it.numero))
        .map((it) => {
            const base = Number(it.precoBase) || 50;
            // custo entre 30% e 80% do precoBase (margem positiva)
            const valor = Number(
                (base * (0.3 + Math.random() * 0.5)).toFixed(2),
            );
            return {
                numeroItem: it.numero,
                valor,
                dataInicial: '',
                dataFinal: '',
            };
        });
    if (mockedCustos.length === 0) return state;
    return {
        ...state,
        selectItems: {
            ...state.selectItems,
            custos: [...existingCustos, ...mockedCustos],
        },
    };
};

// Quando o caller está na tela de bonificação (paymentConditionId === '100'),
// os itens de condição (C) ficam na tela de venda, não em
// `selectItems.availableItems`. Esta função expõe esses itens para que strategies
// como BonificacaoProporcionalStrategy consigam achar o C. Retorna `undefined`
// fora da bonificação — nesse caso `orderItems` já contém tudo que importa.
const getSaleItemsForStrategy = (state: RootState): ItemResponse[] | undefined => {
    if (state.selectItems.request.paymentConditionId !== '100') return undefined;
    const order = state.bonification?.selectedOrders?.find(
        (c) => c.key === state.selectItems.draftRef,
    );
    return (order?.availableItems as ItemResponse[] | undefined);
};

export const fetchAvailableItems = createAsyncThunk<
    ItemResponse[],
    ItemRequest
>('select-items/fetchAvailableItems', async (request, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;

        let branchCode = state.selectItems?.order?.orderType?.platform.id

        if (state?.selectItems?.orderApi?.pedidoCopia && state?.selectType?.selectedPlatform?.id) {
            branchCode = state.selectType.selectedPlatform.id
        }

        const isSaldoLiga = IS_BR && state.balance.useBalance

        const orderDate = state.selectItems?.order?.date || state?.selectItems?.orderApi?.dataProgramada;

        const extraFetchParams: FetchParams = {};
        for (const id of state.campaign.selectedCampaigns) {
            const campaign = state.campaign.availableCampaignForOrder.find(c => c.id === id);
            if (!campaign) continue;
            const strategy = getCampaignStrategy(campaign.tipo);
            if (!strategy) continue;
            const params = strategy.requiresExtraFetchParams(campaign, { orderDate });
            Object.assign(extraFetchParams, params);
        }

        const params = toQueryParameters({
            codigoCliente: state.selectItems.order.customer.code,
            codigoFilial: branchCode,
            codigoPrazo: request.paymentConditionId,
            tipoCliente: state.selectItems.order.customer.typeId,
            tradeCliente: state.selectItems.order.customer.trade ?? '',
            zonaCliente: state.selectItems.order.customer.state,
            moeda: request.codigoMoeda ?? state.selectItems.selectedMoeda.codigo,
            ...(state.login.currentUser.itensEspecificos && {
                limitado: state.login.currentUser.itensEspecificos,
            }),
            saldoLiga: isSaldoLiga,
            ...(extraFetchParams.mes !== undefined && { mes: extraFetchParams.mes }),
        });

        console.debug(params, request);

        const response = await axios.get<ItemResponseApi[]>(
            '/item/byusuario/?' + params,
        );

        thunkAPI.dispatch(updateItemsRequest(request));

        if (request.paymentConditionId !== '100') {
            const campaignsNewList = await thunkAPI.dispatch(
                fetchCampaignsForOrder({
                    paymentConditionId: request.paymentConditionId,
                    date: state.selectItems?.order?.date || state?.selectItems?.orderApi?.dataProgramada,
                    clientState: state.selectItems.order.customer.state
                })
            ).unwrap()

            // apos o fetch de campanhas, verifica se tem alguma campanha selecionada
            // se houver, deve verificar se a nova lista de campanhas há as campanhas selecionadas
            // se houver, nao faz nada.
            // se nao houver, deve remove a que não existe nessa nova lista
            const selectedCampainsIds = state.campaign.selectedCampaigns
            if (selectedCampainsIds.length > 0) {
                const campainsToRemoveIds = []
                selectedCampainsIds.forEach(campaignId => {
                    // campanhas que estao selecionadas mas nao existem na nova lista
                    if (!campaignsNewList.find(campaign => campaign.id === campaignId)) {
                        campainsToRemoveIds.push(campaignId)
                    }
                })

                // se tiver diferença, remove
                if (campainsToRemoveIds.length > 0) {
                    const newArrayOfSelectedCampaigns = selectedCampainsIds.filter(campaignId => !campainsToRemoveIds.includes(campaignId))
                    thunkAPI.dispatch(selectCampaign(newArrayOfSelectedCampaigns))
                }

            }

        }


        const { data: dataReponse } = response;

        const instance = mockPrecoBaseIfMissing(
            dataReponse.map((item: ItemResponseApi) =>
                Object.assign(new ItemResponseApi(), item),
            ),
        );

        console.debug({ params, instance }, 'loaded items', params);

        const stateForCalc = buildStateWithMockedCustos(state, instance);

        const itemsInstance = instance
            .map((item) => {
                const itemRef = request?.tempItems?.find(
                    (i) => i.number === item.numero,
                );

                const newItem = item.toItemResponse(itemRef)

                return newItem;
            })

        const items = itemsInstance
            .map((i) => {
                const [itemResult] = calculateItemCost(
                    { ...i },
                    false,
                    stateForCalc,
                    params,
                );
                const itemResultWithDiscount: ItemResponse = JSON.parse(
                    JSON.stringify({ ...itemResult }),
                );
                return itemResultWithDiscount;
            });


        const itemsByNumber = new Map<string, number>();
        for (const i of items) {
            itemsByNumber.set(i.number, (itemsByNumber.get(i.number) ?? 0) + 1);
        }
        const itemsById = new Map<number, number>();
        for (const i of items) {
            itemsById.set(i.id, (itemsById.get(i.id) ?? 0) + 1);
        }

        setTimeout(() => {
            thunkAPI.dispatch(calculateTotalValue());
        }, 1000);

        const refreshedState = thunkAPI.getState() as RootState;
        const selectedStrategyCampaigns = refreshedState.campaign.availableCampaignForOrder
            .filter((c) => refreshedState.campaign.selectedCampaigns.includes(c.id))
            .filter((c) => !!getCampaignStrategy(c.tipo));

        let finalItems = items;
        if (selectedStrategyCampaigns.length > 0) {

            for (const [campaignIndex, campaign] of selectedStrategyCampaigns.entries()) {
                const strategy = getCampaignStrategy(campaign.tipo);
                if (!strategy) continue;
                if (!strategy.acceptsPaymentCondition(refreshedState.selectItems.request.paymentConditionId)) continue;

                const condByNumero = new Map<string, typeof campaign.condicoes[number]>();
                for (const c of campaign.condicoes) {
                    if (c.campo !== 'ATINGIR_COTA') continue;
                    condByNumero.set((c.numeroItem ?? '').toLowerCase(), c);
                }
                const resultByNumero = new Map<string, typeof campaign.resultados[number]>();
                for (const r of campaign.resultados) {
                    if (!r.numeroItem) continue;
                    resultByNumero.set(r.numeroItem.toLowerCase(), r);
                }

                finalItems = finalItems.map((item) => {
                    const key = (item.number ?? '').toLowerCase();
                    const cond = condByNumero.get(key);
                    const result = resultByNumero.get(key);
                    if (!cond && !result) return item;
                    const updated: ItemResponse = { ...item, campaign, campaignIndex };
                    if (cond) updated.condition = cond;
                    if (result) updated.result = result;
                    return updated;
                });

                const recalculated = strategy.recalculateOnQuantityChange(
                    campaign,
                    finalItems,
                    {
                        paymentConditionId: refreshedState.selectItems.request.paymentConditionId,
                        orderDate: refreshedState.selectItems?.order?.date || refreshedState?.selectItems?.orderApi?.dataProgramada,
                        orderItems: finalItems,
                        saleItems: getSaleItemsForStrategy(refreshedState),
                    },
                );
                finalItems = recalculated.map((item) => {
                    if (item.campaign?.id !== campaign.id) return item;
                    const [recalc] = calculateItemCost({ ...item }, false, refreshedState);
                    return recalc;
                });
            }
        }

        return finalItems;
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

export const fetchSemaphores = createAsyncThunk<Semaphore[]>(
    'select-items/fetchSemaphores',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get<Semaphore[]>(
                '/semaforo/byusuario',
            );

            return response.data;
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchPdiscqt = createAsyncThunk<void>(
    'select-items/fetchPdiscqt',
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState() as RootState;
            const customer = state?.selectCustomer?.customer;

            if (!customer?.typeId) {
                return
            }

            const response = await axios.get<Pdiscqt[]>(
                '/pdiscqt/current/tipocliente/' + customer?.typeId,
            );

            dispatch(updatePdiscqt(response.data));
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return rejectWithValue(message);
        }
    },
);

export const fetchPdiscqtWithType = createAsyncThunk<void, string | undefined>(
    'select-items/fetchPdiscqt',
    async (req, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState() as RootState;
            const customer = state?.selectCustomer?.customer;

            const type = customer?.typeId || req

            if (!type) {
                throw new Error("Customer not found")
            }

            const response = await axios.get<Pdiscqt[]>(
                '/pdiscqt/current/tipocliente/' + type,
            );

            const { data: dataReponse } = response;

            dispatch(updatePdiscqt(dataReponse));
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return rejectWithValue(message);
        }
    },
);

export const fetchConvenio = createAsyncThunk<Convenio[]>(
    'select-items/fetchConvenio',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get<Convenio[]>('/convenio100/all');

            return response.data;
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchCustos = createAsyncThunk<Custo[], CustoRequest | void>(
    'select-items/fetchCustos',
    async (req, thunkAPI) => {
        try {
            const params = toQueryParameters({
                moeda: req ? req.moeda : undefined,
            });

            const response = await axios.get<Custo[]>('/custo/current?' + params);

            const { data: dataReponse } = response;

            return dataReponse;
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchMoedas = createAsyncThunk<Moeda[]>(
    'select-items/fetchMoedas',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get<Moeda[]>('/moeda/all');

            const { data: dataReponse } = response;

            return dataReponse;
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchMoedasSimbolos = createAsyncThunk<MoedaSimbolo[]>(
    'select-items/fetchMoedasSimbolo',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get<MoedaSimbolo[]>('/moeda/simbolo');

            const { data: dataReponse } = response;

            return dataReponse;
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

interface SendOrderProps {
    requestOrder?: Partial<OrderRequest>;
    save?: boolean;
    bonificate?: boolean;
}

export const cloneApiOrder = createAsyncThunk<
    void,
    ThunkCallback<string>
>('select-items/saveOrder', async (req, thunkAPI) => {
    try {

        const response = await axios.post<OrderResponse>(`/pedido/copia/${req.data}`);

        if (response.status === 200) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'success',
                    message: translate('general.orderCreatedAndDraft'),
                    title: translate('general.success'),
                    notificationKey: req.notificationKey,
                    callback: () => req.onSuccess(response.data.pedidoId),
                }),
            );
        }
    } catch (e) {
        if (e.response) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'error',
                    message: e.response.data[0].msgUsuario,
                    title: translate('general.errorT'),
                }),
            );
        }
        return thunkAPI.rejectWithValue('');
    }
});

export interface ApproverOrder {
    ordem: number;
    usuarioId: number;
    usuarioNome: string;
}

export const fetchApproversForOrder = async (
    rootState: RootState,
    ref?: string,
): Promise<ApproverOrder[]> => {
    try {
        const { selectItems, campaign, bonification } = rootState;
        const { availableItems, request, order, selectedMoeda } = selectItems;
        const { selectedOrders } = bonification;
        const { selectedCampaigns, availableCampaignForOrder } = campaign;

        const requestApi = new OrderRequestApi(
            { ...order, codigoMoeda: selectItems?.selectedMoeda?.codigo },
            availableItems.filter((i) => i.quantity > 0),
            request,
            undefined,
            undefined,
            selectedCampaigns,
            availableCampaignForOrder,
        );

        if (ref) {
            requestApi.itensBonificacao = requestApi.itens;
            requestApi.itens = selectedOrders
                .find((c) => `${c.key}` === `${ref}`)
                .availableItems.map<Item>((item) => {
                    return {
                        desconto: item.discount,
                        descricaoItem: item.description,
                        ipi: item.ipi,
                        multiplo: item.quantityPresentation,
                        quantidade: item.quantity,
                        numeroItem: item.number,
                        precoTotal: item.subtotal,
                        precoUnitario: item.valor,
                        promocoes: Number(item.promotion),
                        quantidadeTotal: item.totalQuantity,
                        valorIpi: Number(item.subtotalipi),
                        valorSuframa: item.suframa,
                        valorTrade: item.valorTrade,
                        ordem: item.order,
                        semaforoCor: item.semaforo,
                        semaforoNome: item.semaforoNome,
                        precoUnitarioSemDesconto: item.valorsemdesconto,
                        precoUnitarioSemCampanha: item.valorsemdesconto,
                        precoUnitarioSemCampanhaSemDesconto:
                            item.valorsemdesconto,
                        campanhaId: item.campaign?.id,
                        codigoLinhaItem: item.codigoLinha,
                        codigoTipoTransporte: item.codigoTipoTransporte,
                    };
                });
        }
        const response = await axios.post<ApproverOrder[]>(
            '/pedido/previsaoaprovadores',
            requestApi,
        );

        return response.data;
    } catch (e) {
        console.error(e);
        return [];
    }
};

export const saveApiOrder = createAsyncThunk<
    void,
    ThunkCallback<SendOrderProps>
>('select-items/saveOrder', async (req, thunkAPI) => {
    try {
        const { selectItems, campaign, selectType, selectSeller, balance } = thunkAPI.getState() as RootState;
        const { availableItems, request, order, orderApi } = selectItems;
        const { selectedCampaigns, availableCampaignForOrder } = campaign;
        const { data } = req;
        const { save, bonificate } = data;

        if (save) {
            const selectedCampaignObjs = availableCampaignForOrder.filter(
                (c) => selectedCampaigns.includes(c.id),
            );
            const submitErrors: string[] = [];
            for (const campaignObj of selectedCampaignObjs) {
                const strategy = getCampaignStrategy(campaignObj.tipo);
                if (!strategy) continue;
                const errors = strategy.validateForSubmit(
                    campaignObj,
                    availableItems,
                    {
                        paymentConditionId: request.paymentConditionId,
                        orderDate: order?.date || orderApi?.dataProgramada,
                        orderItems: availableItems,
                    },
                );
                for (const err of errors) submitErrors.push(err.message);
            }
            if (submitErrors.length > 0) {
                thunkAPI.dispatch(
                    addNotification({
                        type: 'error',
                        message: submitErrors.join('\n'),
                        title: translate('general.errorT'),
                        notificationKey: req.notificationKey,
                    }),
                );
                return thunkAPI.rejectWithValue(submitErrors.join('\n'));
            }
        }

        const requestApi = new OrderRequestApi(
            { ...order, codigoMoeda: selectItems?.selectedMoeda?.codigo },
            availableItems.filter((i) => i.quantity > 0),
            { ...request, paymentConditionId: order.paymentCondition.id },
            undefined,
            undefined,
            selectedCampaigns,
            availableCampaignForOrder,
        );

        if (balance.useBalance) {
            Object.assign(requestApi, {
                saldoLiga: true
            })
        }

        if (orderApi?.pedidoCopia) { // if copy

            // save seller - vendedor
            if (selectSeller?.selectedSeller?.codigoJDE
                && selectItems.orderApi.usuarioId !== selectSeller.selectedSeller.id
            ) {
                Object.assign(requestApi, { codigoJDE: selectSeller?.selectedSeller?.codigoJDE })
            }

            // save platform - filial
            if (selectType?.selectedPlatform?.id
                && selectItems.orderApi.codigoFilial !== selectType.selectedPlatform.id
            ) {
                await thunkAPI.dispatch(requestChangeBranch({
                    codigoFilial: parseInt(selectType.selectedPlatform.id),
                    pedidoId: orderApi.id
                })).unwrap()
                Object.assign(requestApi, { codigoFilial: parseInt(selectType.selectedPlatform.id) })
            }
        }

        console.info(requestApi, 'API request');

        if (selectItems.draftRef) {
            const response = await axios.put(
                '/pedido/' + selectItems.draftRef,
                requestApi,
            );

            if (response.status === 200) {
                if (bonificate || save) {
                    req.onSuccess();
                } else
                    thunkAPI.dispatch(
                        addNotification({
                            type: 'success',
                            message: translate('general.orderUpdatedAndDraft'),
                            title: translate('general.success'),
                            notificationKey: req.notificationKey,
                            callback: req.onSuccess,
                        }),
                    );

                if (bonificate) {
                    thunkAPI.dispatch(
                        fetchOrdersToBonitifaction({
                            customerId: order.customer.id,
                            platformId: Number(order.orderType.platform.id),
                            moeda: selectItems?.selectedMoeda?.codigo
                        }),
                    );

                    thunkAPI.dispatch(
                        updateOrderState({
                            draftRef: selectItems.draftRef,
                        }),
                    );
                }
                if (save) {
                    thunkAPI.dispatch(
                        sendOrder({
                            notificationKey: req.notificationKey,
                            data: [Number(selectItems.draftRef)],
                        }),
                    );
                }
            }
        }

        if (!selectItems.draftRef) {
            const response = await axios.post<OrderResponse>(
                '/pedido',
                requestApi,
            );

            if (response.status === 201) {
                if (bonificate || save) {
                    req.onSuccess();
                } else
                    thunkAPI.dispatch(
                        addNotification({
                            type: 'success',
                            message: translate('general.orderCreatedAndDraft'),
                            title: translate('general.success'),
                            notificationKey: req.notificationKey,
                            callback: () => req.onSuccess(),
                        }),
                    );

                if (bonificate) {
                    thunkAPI.dispatch(
                        fetchOrdersToBonitifaction({
                            customerId: order.customer.id,
                            platformId: Number(order.orderType.platform.id),
                            moeda: selectItems?.selectedMoeda?.codigo
                        }),
                    );

                    thunkAPI.dispatch(
                        updateOrderState({
                            draftRef: `${response.data.pedidoId}`,
                        }),
                    );
                }

                if (save) {
                    thunkAPI.dispatch(
                        sendOrder({
                            notificationKey: req.notificationKey,
                            data: [response.data.pedidoId],
                        }),
                    );
                }
            }
        }
    } catch (e) {
        let message = ''
        if (e?.response?.data && e.response.data.length > 0 && e.response.data[0].msgUsuario) {
            message = e.response.data[0].msgUsuario;
        } else {
            message = translate('general.error')
        }

        thunkAPI.dispatch(
            addNotification({
                type: 'error',
                message,
                title: translate('general.errorT'),
                notificationKey: req.notificationKey,
                callback: req.onError ? req.onError : undefined,
            }),
        );

        return thunkAPI.rejectWithValue(message);
    }
});

export const sendOrder = createAsyncThunk<void, ThunkCallback<number[]>>(
    'select-items/sendOrder',
    async (req, thunkAPI) => {
        try {
            const requestSend = await axios.post<OrderResponse>(
                '/pedido/send',
                req.data,
            );

            if (requestSend.status === 200)
                thunkAPI.dispatch(
                    addNotification({
                        type: 'success',
                        message: translate('general.orderSended'),
                        title: translate('general.success'),
                        notificationKey: req.notificationKey,
                        callback: req.onSuccess ? req.onSuccess : undefined,
                    }),
                );
            thunkAPI.dispatch(fetchDrafts({ size: 999 }));
        } catch (e) {
            if (e.response) {
                thunkAPI.dispatch(
                    addNotification({
                        type: 'error',
                        message: e.response.data[0].msgUsuario,
                        title: translate('general.errorT'),
                    }),
                );
            }
            return thunkAPI.rejectWithValue('');
        }
    },
);

export const fetchAvailablePaymentConditions = createAsyncThunk<
    PaymentConditionResponse[]
>('select-type/fetchAvailablePaymentConditions', async (req, thunkAPI) => {
    try {
        const response = await axios.get<PaymentConditionResponseApi[]>(
            '/prazo/byusuario',
        );


        const { data, status } = response;

        if (status === 200) {
            const result = data.map((u) =>
                Object.assign(new PaymentConditionResponseApi(), u),
            );
            return result.map((c) => c.toPaymentConditionResponse());
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

export const fetchPaymentCondition = createAsyncThunk<
    PaymentConditionResponse,
    ThunkCallback<number>
>('select-type/fetchPaymentCondition', async (req, thunkAPI) => {
    try {
        const response = await axios.get<PaymentConditionResponseApi>(
            `/prazo/byusuario/cliente/${req.data}`,
        );

        const data = Object.assign(
            new PaymentConditionResponseApi(),
            response.data,
        );

        if (response.status === 200) {
            return data.toPaymentConditionResponse();
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

export const updateCampaignItems = createAsyncThunk<ItemResponse[]>(
    'select-items/updateCampaignItems',
    async (req, { dispatch, getState }) => {
        const internalState = getState() as RootState;
        const state = JSON.parse(JSON.stringify(internalState)) as RootState;

        const list = [];
        const stillSelectedCampaignIds = new Set<number>(state.campaign.selectedCampaigns);
        const itemsInList = [...state.selectItems.availableItems].map((c) => {
            // Item era resultado de uma strategy campaign que foi desmarcada:
            // zera o desconto pra que o subtotal volte ao valor sem o desconto da campanha.
            const isStaleStrategyResult =
                !!c.result &&
                !!c.campaign &&
                !!getCampaignStrategy(c.campaign.tipo) &&
                !stillSelectedCampaignIds.has(c.campaign.id);

            const base = {
                ...c,
                campaign: undefined,
                campaignIndex: undefined,
                disabled: false,
                min: undefined,
                condition: undefined,
                result: undefined,
                discount: isStaleStrategyResult ? 0 : c.discount,
            };

            if (isStaleStrategyResult) {
                const [recalc] = calculateItemCost({ ...base }, false, state);
                return recalc;
            }
            return base;
        });

        const campaigns = state.campaign.availableCampaignForOrder.filter((c) =>
            state.campaign.selectedCampaigns.includes(c.id),
        );

        const itemsOfCampain = []
        if (state.campaign.oldSeletedCampain) {
            const oldCampain = state.campaign.availableCampaignForOrder.find((c) =>
                state.campaign.oldSeletedCampain === c.id,
            );

            dispatch(oldSeletedCampain(undefined))

            for (const condition of oldCampain?.condicoes) {
                if (condition.campo !== 'QUANTIDADE_ITEM') continue;
                const itemIndex = itemsInList.findIndex(
                    (i) =>
                        i.number.toLocaleLowerCase() ===
                        condition.numeroItem.toLocaleLowerCase(),
                );

                if (
                    itemIndex === -1 ||
                    state.selectItems.request.paymentConditionId === '100'
                )
                    continue;

                const item = { ...itemsInList[itemIndex] };
                item.campaign = undefined;
                item.campaignIndex = undefined;
                item.condition = undefined;
                item.disabled = false;
                item.result = undefined;

                item.quantity = 0;
                item.min = undefined;

                const [itemResult] = calculateItemCost(
                    { ...item },
                    false,
                    state,
                );

                itemsOfCampain.push(itemResult);
            }
        }

        const itemsRemovedCampainInfo: ItemResponse[] = [
            ...itemsOfCampain,
            ...itemsInList.filter((i) => !itemsOfCampain.some((c) => c.id === i.id)),
        ]


        if (campaigns.length === 0) {
            // Sem campanhas: o subtotal precisa ser recalculado pra refletir os
            // itens que perderam o desconto da strategy desmarcada.
            setTimeout(() => {
                dispatch(calculateTotalValue());
            }, 1000);
            return itemsRemovedCampainInfo.removeDuplicatesById();
        }

        for (const [index, campaign] of campaigns.reverse().entries()) {
            const strategy = getCampaignStrategy(campaign.tipo);

            // C-side QUANTIDADE_ITEM: roda independente de strategy/legacy.
            // Para BonificacaoProporcionalStrategy, o C precisa aparecer na tela
            // de venda mesmo que a strategy (R-side) só ative com paymentCond === '100'.
            for (const condition of campaign.condicoes) {
                if (condition.campo !== 'QUANTIDADE_ITEM') continue;
                if (state.selectItems.request.paymentConditionId === '100') continue;
                const itemIndex = itemsRemovedCampainInfo.findIndex(
                    (i) =>
                        i.number.toLocaleLowerCase() ===
                        condition.numeroItem.toLocaleLowerCase(),
                );
                if (itemIndex === -1) continue;

                const item = { ...itemsRemovedCampainInfo[itemIndex] };
                item.campaign = campaign;
                item.campaignIndex = index;
                item.condition = condition;
                item.min = Number(condition.valor);
                item.quantity = item.quantity && item.quantity >= item.min ? item.quantity : Number(condition.valor);

                const [itemResult] = calculateItemCost(
                    { ...item },
                    false,
                    state,
                );

                list.push(itemResult);
            }

            if (strategy) {
                if (!strategy.acceptsPaymentCondition(state.selectItems.request.paymentConditionId)) {
                    continue;
                }

                const pushedIds = new Set<number>();
                const upsert = (entry: ItemResponse) => {
                    const existingIdx = list.findIndex((i) => i.id === entry.id);
                    if (existingIdx !== -1) {
                        list[existingIdx] = entry;
                    } else {
                        list.push(entry);
                    }
                    pushedIds.add(entry.id);
                };

                for (const condition of campaign.condicoes) {
                    if (condition.campo !== 'ATINGIR_COTA') continue;
                    const itemIndex = itemsRemovedCampainInfo.findIndex(
                        (i) =>
                            i.number.toLocaleLowerCase() ===
                            condition.numeroItem.toLocaleLowerCase(),
                    );
                    if (itemIndex === -1) continue;

                    const source = itemsRemovedCampainInfo[itemIndex];
                    if (pushedIds.has(source.id)) {
                        continue;
                    }

                    const item = { ...source };
                    item.campaign = campaign;
                    item.campaignIndex = index;
                    item.condition = condition;

                    const [itemResult] = calculateItemCost(
                        { ...item },
                        false,
                        state,
                    );
                    upsert(itemResult);
                }

                for (const result of campaign.resultados) {
                    if (!result.numeroItem) continue;
                    const itemIndex = itemsRemovedCampainInfo.findIndex(
                        (i) =>
                            i.number.toLocaleLowerCase() ===
                            result.numeroItem.toLocaleLowerCase(),
                    );
                    if (itemIndex === -1) continue;

                    const source = itemsRemovedCampainInfo[itemIndex];
                    const existing = list.find((i) => i.id === source.id);
                    const base = existing ?? { ...source };
                    const item = strategy.applyResultToItem(
                        { ...base, campaignIndex: index },
                        result,
                        campaign,
                        {
                            paymentConditionId: state.selectItems.request.paymentConditionId,
                            orderDate: state.selectItems?.order?.date || state?.selectItems?.orderApi?.dataProgramada,
                            orderItems: itemsRemovedCampainInfo,
                            saleItems: getSaleItemsForStrategy(state),
                        },
                    );

                    const [itemResult] = calculateItemCost(
                        { ...item },
                        false,
                        state,
                    );
                    upsert(itemResult);
                }

                continue;
            }

        }
        setTimeout(() => {
            dispatch(calculateTotalValue());
        }, 1000);

        let newList: ItemResponse[] = [
            ...list,
            ...itemsRemovedCampainInfo.filter((i) => !list.some((c) => c.id === i.id)),
        ]

        for (const campaign of campaigns) {
            const strategy = getCampaignStrategy(campaign.tipo);
            if (!strategy) continue;
            if (!strategy.acceptsPaymentCondition(state.selectItems.request.paymentConditionId)) continue;

            const updated = strategy.recalculateOnQuantityChange(
                campaign,
                newList,
                {
                    paymentConditionId: state.selectItems.request.paymentConditionId,
                    orderDate: state.selectItems?.order?.date || state?.selectItems?.orderApi?.dataProgramada,
                    orderItems: newList,
                    saleItems: getSaleItemsForStrategy(state),
                },
            );

            newList = updated.map((item) => {
                if (item.campaign?.id !== campaign.id) return item;
                const [recalc] = calculateItemCost({ ...item }, false, state);
                return recalc;
            });
        }

        const byKey = new Map<string, ItemResponse>();
        const keyOf = (i: ItemResponse): string => `${i.number ?? i.id}`;
        for (const item of newList) {
            const k = keyOf(item);
            const existing = byKey.get(k);
            if (!existing) {
                byKey.set(k, item);
                continue;
            }
            const existingGross = existing.valorGross;
            const incomingGross = item.valorGross;
            const preferIncoming =
                (existingGross == null && incomingGross != null) ||
                (existingGross == null && incomingGross == null && !existing.campaign && !!item.campaign);
            if (preferIncoming) byKey.set(k, item);
        }
        const deduped = Array.from(byKey.values());

        return deduped
    },
);

export const updateItemApi = createAsyncThunk<void, Partial<ItemResponse>>(
    'select-items/updateItemApi',
    async (req, { dispatch, getState }) => {
        try {
            const internalState = getState() as RootState;
            const { id, ...rest } = req;
            const index = internalState.selectItems.availableItems.findIndex(
                (i) => i.id === id,
            );

            let item = internalState.selectItems.availableItems[index];

            if (item) {
                item = { id, ...item, ...rest };

                if (rest.quantity && item.kit != null) {
                    const itensKit =
                        internalState.selectItems.availableItems.filter(
                            (i) => {
                                return `${i.number}` == `${item.kit}`;
                            },
                        );

                    itensKit.forEach((itemKit) => {
                        const tempI = { ...itemKit };
                        tempI.quantity = item.compkit * item.quantity;

                        const [itemResult] = calculateItemCost(
                            { ...tempI },
                            false,
                            internalState,
                        );

                        dispatch(updateItem(itemResult));
                    });
                }

                const [itemResult] = calculateItemCost(
                    { ...item },
                    false,
                    internalState,
                );

                console.debug(itemResult, item);
                console.debug(rest);

                dispatch(updateItem(itemResult));

                const refreshedState = getState() as RootState;
                const selectedCampaigns = refreshedState.campaign.availableCampaignForOrder.filter(
                    (c) => refreshedState.campaign.selectedCampaigns.includes(c.id),
                );

                for (const campaign of selectedCampaigns) {
                    const strategy = getCampaignStrategy(campaign.tipo);
                    if (!strategy) continue;
                    if (!strategy.acceptsPaymentCondition(refreshedState.selectItems.request.paymentConditionId)) continue;

                    const recalculated = strategy.recalculateOnQuantityChange(
                        campaign,
                        refreshedState.selectItems.availableItems,
                        {
                            paymentConditionId: refreshedState.selectItems.request.paymentConditionId,
                            orderDate: refreshedState.selectItems?.order?.date || refreshedState?.selectItems?.orderApi?.dataProgramada,
                            orderItems: refreshedState.selectItems.availableItems,
                            saleItems: getSaleItemsForStrategy(refreshedState),
                            triggeredByItemId: id as number,
                        },
                    );

                    for (const updated of recalculated) {
                        const original = refreshedState.selectItems.availableItems.find((i) => i.id === updated.id);
                        if (!original) continue;
                        if (
                            original.discount === updated.discount &&
                            original.result?.numeroItem === updated.result?.numeroItem &&
                            original.campaign?.id === updated.campaign?.id &&
                            original.quantity === updated.quantity
                        ) continue;

                        const [recalc] = calculateItemCost({ ...updated }, false, refreshedState);
                        dispatch(updateItem(recalc));
                    }
                }

                dispatch(calculateTotalValue());
            }
        } catch (e) {
            //
        }
    },
);

export const calculateTotalValue = createAsyncThunk<void>(
    'select-items/calculateTotalValue',
    async (req, { dispatch, getState }) => {
        try {
            const internalState = getState() as RootState;
            console.debug(internalState, 'estado');
            dispatch(updateOrder(calculateOrderTotalValue(internalState)));
        } catch (e) {
            //
        }
    },
);

interface IFetchOrderProps {
    id: number;
    order?: IOrderItemsSelectorState
}

export const fetchOrder = createAsyncThunk<
    OrderResponseApi,
    ThunkCallback<IFetchOrderProps>
>('select-type/fech-order', async (req, thunkAPI) => {
    const internalState = thunkAPI.getState() as RootState;
    try {
        const response = await axios.get<OrderResponseApi>(`/pedido/${req.data.id}`);

        const data = Object.assign(
            new OrderResponseApi(),
            response.data,
        );

        const orderRequest = data.toOrderRequest()

        thunkAPI.dispatch(
            updateOrderState({
                ...req.data.order,
                draftRef: data.id.toString(),
                orderApi: data,
                order: {
                    ...orderRequest,
                    tempItems: orderRequest.tempItems,
                },
            }),
        )
        thunkAPI.dispatch(updateUseBalance(orderRequest.saldoLiga));

        if (IS_BR && orderRequest.saldoLiga && orderRequest.orderType.type.id === GROUP_TYPES.V2) {
            thunkAPI.dispatch(fetchBalance({
                codigoCliente: orderRequest.customer.code,
                divisao: orderRequest.division,
            }))
        }

        thunkAPI.dispatch(setOrderType(orderRequest.orderType));

        if (orderRequest.orderType) {
            thunkAPI.dispatch(addAvailablePlatform({
                id: orderRequest.orderType.platform?.id,
                description: orderRequest.orderType.platform.description,
                conven: undefined
            }));
        }
        thunkAPI.dispatch(selectCustomer(orderRequest.customer));

        const customer = await thunkAPI.dispatch(fetchCustomer(orderRequest.customer.code)).unwrap()
        thunkAPI.dispatch(selectCustomer(customer))

        let addCustomerSelected;
        if (orderRequest.addCustomer) {
            addCustomerSelected = await thunkAPI.dispatch(fetchCustomer(data.codigoClienteEntrega)).unwrap()
        }

        thunkAPI.dispatch(updateOrder({ customer, addCustomerSelected }))

        if (!customer) {
            thunkAPI.dispatch(
                addNotification({
                    message: translate("general.clientNotFound"),
                    title: translate('general.notfound'),
                    type: 'error',
                }),
            );
        }

        if (!data?.codigoMoeda && !internalState.selectItems.selectedMoeda) {
            if (internalState.selectItems.moedas.length === 1) {
                const moeda = internalState.selectItems.moedas[0]
                const simbol = internalState.selectItems.moedaSimbolos.find(item => item.codigoMoeda === moeda.codigo)
                if (simbol) {
                    thunkAPI.dispatch(updateMoeda({
                        codigo: moeda.codigo,
                        descricao: moeda.descricao,
                        simbolo: simbol.simbolo
                    }))
                }
            }
        }

        return data;
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

interface VerifyCanSaveReasonsProps {
    marketing?: boolean,
    bonification?: boolean
}


export const verifyCanSaveReasons = createAsyncThunk<void, VerifyCanSaveReasonsProps>(
    'select-items/verifyCanSaveReasons',
    async (req, { getState, dispatch }) => {
        const state = getState() as RootState;
        const { availablePaymentConditions, availableItems, request, order, selectedMoeda } =
            state.selectItems;
        const { selectedOrders } = state.bonification;
        const { minOrderValue } = state.minOrder;
        const { availableParameters } = state.parameters;
        const configMinOrder = availableParameters.find(c => c.id === 'VALOR_MINIMO_PEDIDO')

        if (minOrderValue && minOrderValue.length > 0) {
            let resultMessage = '';
            for (const minValue of minOrderValue) {
                const itemsSum = availableItems
                    .filter(
                        (i) =>
                            i.quantity > 0 &&
                            i.codigoTipoTransporte ===
                            minValue.codigoTipoTransporte,
                    )
                    .reduce((acc, item) => acc + item.subtotal, 0);
                if (itemsSum < minValue.valorMinimo)
                    resultMessage += `${translate('columns.transportType')}: ${minValue.codigoTipoTransporte
                        } - ${translate('forms.labels.minValue')}: ${formatMoney(
                            selectedMoeda?.simbolo,
                            minValue.valorMinimo,
                        )} \n`;
            }

            if (resultMessage) {
                dispatch(updateCanSaveReason(`${translate('general.minValueTransport')} \n` + resultMessage));
            } else {
                dispatch(updateCanSaveReason(''));
            }
            return
        }

        if (!availableItems.some((i) => i.quantity > 0)) {
            dispatch(updateCanSaveReason(""));
            return
        };

        if (IS_ARG && !req.marketing && !req.bonification && availableParameters && configMinOrder) {
            const itemsSum = availableItems.reduce((acc, item) => acc + item.subtotal, 0);
            if (itemsSum < Number(configMinOrder.valor)) {
                const message = `${translate('forms.labels.minValue')} ${formatMoney(
                    selectedMoeda?.simbolo,
                    Number(configMinOrder.valor),
                )} ${translate('general.notReached')}.`;

                dispatch(updateCanSaveReason(message));

                return
            }
        }

        if (availablePaymentConditions.length === 0) {
            dispatch(updateCanSaveReason(""));
            return
        };

        const paymentCondition = availablePaymentConditions.find(
            (pc) =>
                pc.id ===
                (selectedOrders.length > 0 ? 100 : request.paymentConditionId),
        );

        if (paymentCondition === undefined) {
            dispatch(updateCanSaveReason(""));
            return
        };
        // Calcula o valor total do pedido diretamente dos itens para evitar
        // race condition com o calculateTotalValue (que roda com setTimeout).
        const computedOrderValue = availableItems.reduce(
            (acc, item) => acc + Number((item.subtotal ?? 0).toFixed(2)) - (item.suframa ?? 0),
            0,
        );

        if (paymentCondition.minOrderValue > computedOrderValue) {
            const message = `${translate('general.minValueOf')} ${formatMoney(
                selectedMoeda?.simbolo,
                paymentCondition.minOrderValue,
            )} ${translate('general.notReached')} ${translate('general.forThisPaymentMethod')}.`;
            dispatch(updateCanSaveReason(message));
            return
        }

        const selectedCampaignsList = state.campaign.availableCampaignForOrder.filter(
            (c) => state.campaign.selectedCampaigns.includes(c.id),
        );
        const submitErrors: string[] = [];
        for (const campaign of selectedCampaignsList) {
            const strategy = getCampaignStrategy(campaign.tipo);
            if (!strategy) continue;
            const errors = strategy.validateForSubmit(
                campaign,
                availableItems,
                {
                    paymentConditionId: request.paymentConditionId,
                    orderDate: order?.date || state?.selectItems?.orderApi?.dataProgramada,
                    orderItems: availableItems,
                },
            );
            for (const err of errors) submitErrors.push(err.message);
        }
        if (submitErrors.length > 0) {
            dispatch(updateCanSaveReason(submitErrors.join('\n')));
            return
        }

        dispatch(updateCanSaveReason(''));
    },
);


export interface IOrderItemsSelectorState {
    canSaveReason?: string;
    draftRef?: string;
    lastUpdate?: string;
    availableItems: ItemResponse[];
    semaphores: Semaphore[];
    pdiscqt: Pdiscqt[];
    availablePaymentConditions: PaymentConditionResponse[];
    paymentCondition?: PaymentConditionResponse,
    filter?: IFilterSelectItems;
    request: ItemRequest;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage: string;
    order?: Partial<OrderRequest>;
    orderApi?: OrderResponseApi,

    selectedMoeda?: SelectedMoeda,

    isFetchingMoeda: boolean;
    isFetchingMoedaSimbolo: boolean;
    isFetchingPaymentCondition: boolean;
    isSuccessPaymentCondition: boolean;
    isErrorPaymentCondition: boolean;
    errorMessagePaymentCondition: string;
    convenios: Convenio[];
    custos: Custo[];
    moedas: Moeda[];
    moedaSimbolos: MoedaSimbolo[];
    haveIpi: boolean;
    discountPanteci: number;
    isSubmitting: boolean;
}

export const initialItemsState: IOrderItemsSelectorState = {
    availableItems: [],
    semaphores: [],
    availablePaymentConditions: [],
    pdiscqt: [],
    convenios: [],
    custos: [],
    moedas: [],
    moedaSimbolos: [],
    request: {},
    isFetching: true,
    isSuccess: false,
    isError: false,
    isFetchingPaymentCondition: true,
    isFetchingMoeda: true,
    isFetchingMoedaSimbolo: true,
    isSuccessPaymentCondition: false,
    isErrorPaymentCondition: false,
    errorMessage: '',
    errorMessagePaymentCondition: '',
    order: { ...defaultOrderRequest },
    filter: {
        filterBy: 'todos',
        filterValue: '',
    },
    haveIpi: false,
    discountPanteci: 0,
    isSubmitting: false,
};

const selectTypeSlice = createSlice({
    name: 'selectTypeSlice',
    initialState: initialItemsState,
    reducers: {
        clearState: (state) => {
            state = { ...initialItemsState };
            return state;
        },
        updateItemsRequest: (state, action: PayloadAction<ItemRequest>) => {
            state.request = action.payload;

            return state;
        },
        resetAvailableItems: (state) => {
            state.availableItems = [];
            return state;
        },
        filterItems: (
            state,
            action: PayloadAction<Partial<IFilterSelectItems>>,
        ) => {
            state.filter = { ...state.filter, ...action.payload };
        },
        updateOrderState: (
            state,
            action: PayloadAction<Partial<IOrderItemsSelectorState>>,
        ) => {
            state = {
                ...state,
                ...action.payload,
                custos: state.custos,
                moedas: state.moedas,
                moedaSimbolos: state.moedaSimbolos,
                semaphores: state.semaphores,
                convenios: state.convenios,
                pdiscqt: state.pdiscqt,
            };

            return state;
        },
        updateOrder: (state, action: PayloadAction<Partial<OrderRequest>>) => {
            state.order = {
                ...state.order,
                ...action.payload,
                deliveryDate: action.payload.deliveryDate ?? state.order.deliveryDate,
            };
            if (
                action.payload.addCustomer !== undefined &&
                !action.payload.addCustomer
            ) {
                state.order.addCustomerSelected = undefined;
            }
        },
        updatePaymentCondition: (state, action: PayloadAction<OrderRequest['paymentCondition']>) => {
            state.order = {
                ...state.order,
                paymentCondition: action.payload,
            };
        },
        updateOrderPlatform: (state, action: PayloadAction<EntityString>) => {
            state.order.orderType.platform = action.payload;

            return state;
        },
        resetSelectItemsState: (state) => {
            state = { ...initialItemsState };

            return state;
        },
        updateItem: (state, action: PayloadAction<ItemResponse>) => {
            const index = state.availableItems.findIndex(
                (c) => c.id === action.payload.id,
            );
            state.availableItems[index] = action.payload;
        },
        updatePdiscqt: (state, action: PayloadAction<Pdiscqt[]>) => {
            state.pdiscqt = action.payload;
            return state;
        },
        updateMoeda: (state, action: PayloadAction<SelectedMoeda>) => {
            state.selectedMoeda = action.payload;
            return state;
        },
        updateCanSaveReason: (state, action: PayloadAction<string | undefined>) => {
            state.canSaveReason = action.payload;
            return state;
        }
    },
    extraReducers: {
        [fetchAvailableItems.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<ItemResponse[]>,
        ) => {
            /* state.order = { ...state.order, tempItems: [] }; */
            const byKey = new Map<string, ItemResponse>();
            for (const item of payload) {
                const k = `${item.number ?? item.id}`;
                const existing = byKey.get(k);
                if (!existing) {
                    byKey.set(k, item);
                    continue;
                }
                const preferIncoming =
                    existing.valorGross == null && item.valorGross != null;
                if (preferIncoming) byKey.set(k, item);
            }
            const deduped = Array.from(byKey.values());
            state.availableItems = deduped;

            // MOCK_PRECO_BASE: persiste custos mockados para os itens
            // que não vieram em /custo, garantindo que recalculos
            // disparados por updateItemApi (mudança de qtd/desconto/preço)
            // continuem encontrando custo > 0.
            if (MOCK_PRECO_BASE) {
                const existing = new Set(
                    (state.custos ?? []).map((c) => c.numeroItem),
                );
                const missing: Custo[] = deduped
                    .filter((it) => it.number && !existing.has(it.number))
                    .map((it) => {
                        const base = Number(it.precoBase) || 50;
                        const valor = Number(
                            (base * (0.3 + Math.random() * 0.5)).toFixed(2),
                        );
                        return {
                            numeroItem: it.number,
                            valor,
                            dataInicial: '',
                            dataFinal: '',
                        };
                    });
                if (missing.length > 0) {
                    state.custos = [...(state.custos ?? []), ...missing];
                }
            }

            state.isFetching = false;
            state.isSuccess = true;
            return state;
        },
        [fetchAvailableItems.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
        },
        [fetchAvailableItems.pending.toString()]: (state) => {
            state.isFetching = true;
            state.isError = false;
        },

        [fetchSemaphores.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Semaphore[]>,
        ) => {
            state.semaphores = payload.filter(
                (s) =>
                    moment(s.dataInicial).isSameOrBefore(moment()) &&
                    moment(s.dataFinal).add(1, 'days').isSameOrAfter(moment()),
            );
            return state;
        },

        [fetchCustos.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Custo[]>,
        ) => {
            state.custos = payload.filter(
                (s) =>
                    moment(s.dataInicial).isSameOrBefore(moment()) &&
                    moment(s.dataFinal).add(1, 'days').isSameOrAfter(moment()),
            );
            // state.custos = payload;
            return state;
        },

        [fetchMoedas.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Moeda[]>,
        ) => {
            state.moedas = [...payload]
            state.isFetchingMoeda = false
            return state;
        },

        [fetchMoedasSimbolos.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<MoedaSimbolo[]>,
        ) => {
            state.moedaSimbolos = [...payload]
            state.isFetchingMoedaSimbolo = false
            return state;
        },

        [fetchConvenio.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Convenio[]>,
        ) => {
            state.convenios = payload;
            return state;
        },

        [fetchAvailablePaymentConditions.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<PaymentConditionResponse[]>,
        ) => {
            state.availablePaymentConditions = payload.sort(
                (a: any, b: any) => a.id - b.id,
            );
            state.isFetchingPaymentCondition = false;
            state.isSuccessPaymentCondition = true;
            return state;
        },
        [fetchAvailablePaymentConditions.rejected.toString()]: (
            state,
            action,
        ) => {
            state.isFetchingPaymentCondition = false;
            state.isErrorPaymentCondition = true;
            state.errorMessagePaymentCondition = action.payload;
        },
        [fetchAvailablePaymentConditions.pending.toString()]: (state) => {
            state.isFetchingPaymentCondition = true;
            state.isErrorPaymentCondition = false;
        },
        [fetchPaymentCondition.fulfilled.toString()]: (state, { payload }: PayloadAction<PaymentConditionResponse>) => {
            state.paymentCondition = payload;
        },
        [updateCampaignItems.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<ItemResponse[]>,
        ) => {
            const grossByNumber = new Map<string, number>();
            for (const r of state.availableItems) {
                if (r.number && r.valorGross != null) {
                    grossByNumber.set(r.number.toLowerCase(), r.valorGross);
                }
            }
            state.availableItems = payload.map((item) => {
                if (item.valorGross == null && item.number) {
                    const fresh = grossByNumber.get(item.number.toLowerCase());
                    if (fresh != null) return { ...item, valorGross: fresh };
                }
                return item;
            });
            return state;
        },
        [saveApiOrder.pending.toString()]: (
            state,
        ) => {
            state.isSubmitting = true;
            return state;
        },
        [saveApiOrder.fulfilled.toString()]: (
            state,
        ) => {
            state.isSubmitting = false;
            return state;
        },
        [saveApiOrder.rejected.toString()]: (
            state,
        ) => {
            state.isSubmitting = false;
            return state;
        },
    },
});


export const orderItemsSelector = (
    state: RootState,
): IOrderItemsSelectorState => state.selectItems;

export const hasANTECSelector = (state: RootState): number => {
    const isBonification =
        state.bonification.selectedOrders &&
        state.bonification.selectedOrders.length > 0;
    if (isBonification) return -99999;

    return state.selectItems.availableItems.some(
        (i) => i.pANTECI && i.pANTECI !== 0,
    )
        ? state.selectItems.availableItems.filter(
            (i) => i.pANTECI && i.pANTECI !== 0,
        )[0].pANTECI
        : -99999;
};

export const {
    updateItemsRequest,
    updateItem,
    filterItems,
    updateOrder,
    updatePaymentCondition,
    updateOrderState,
    resetSelectItemsState,
    updatePdiscqt,
    updateMoeda,
    updateOrderPlatform,
    resetAvailableItems,
    updateCanSaveReason,
    clearState
} = selectTypeSlice.actions;

export default selectTypeSlice.reducer;
